import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./Kanban1.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function Kanban1(props) {
  const { x1200PxLogo_Icam__20081 } = props;
  const [hasImplemented, setHasImplemented] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({
    // Part 1: Current state - Use of Kanban today
    // Physical flow management
    fluxTypes: "",
    kanbanSystem: "",
    kanbanLoops: "",
    cardsNumber: "",
    kanbanUnits: "",
    
    // Operating logic
    replenishmentFrequency: "",
    cardsRules: "",
    stockoutManagement: "",
    loopsAdjustment: "",
    replenishmentDelay: "",
    
    // Part 2: Desired future state - Target vision of the Kanban system
    // Objectives and ambitions
    performanceObjectives: "",
    kanbanExtension: "",
    mrpReplacement: "",
    stockTargets: "",
    digitalization: "",
    
    // Part 3: Monitoring, gaps and continuous improvement
    // KPIs and governance
    kanbanKPIs: "",
    loopsResponsible: "",
    anomaliesManagement: "",
    parametersReview: "",
    implementedImprovements: "",
    
    // Organization and culture
    operatorsTraining: "",
    suppliersIntegration: "",
    visualSupport: "",
    culturalResistance: "",
    roadmap: ""
  });
  
  const [uploadStatus, setUploadStatus] = useState({
    isUploading: false,
    progress: 0,
    error: null,
    success: false,
    fileName: ""
  });
  const fileInputRef = useRef(null);
  const questionnaireRef = useRef(null);

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // File type validation
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/svg+xml', 
                         'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                         'application/vnd.ms-excel',
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      setUploadStatus({
        ...uploadStatus,
        error: "Unsupported file type. Please upload PDF, JPG, PNG, SVG, Excel or Word.",
        success: false
      });
      return;
    }

    // Size validation (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setUploadStatus({
        ...uploadStatus,
        error: "File is too large. Maximum size: 10MB",
        success: false
      });
      return;
    }

    try {
      setUploadStatus({
        isUploading: true,
        progress: 0,
        error: null,
        success: false,
        fileName: file.name
      });

      // Creating FormData for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('answers', JSON.stringify(answers));

      const xhr = new XMLHttpRequest();
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadStatus(prev => ({
            ...prev,
            progress
          }));
        }
      };

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.response));
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
      });

      xhr.open('POST', '/api/kanban/upload');
      xhr.send(formData);

      const response = await uploadPromise;

      setUploadStatus({
        isUploading: false,
        progress: 100,
        error: null,
        success: true,
        fileName: file.name,
        fileId: response.file_id
      });

    } catch (error) {
      setUploadStatus({
        isUploading: false,
        progress: 0,
        error: "An error occurred during upload. Please try again.",
        success: false,
        fileName: ""
      });
    }
  };

  const handleAnswerChange = (field, value) => {
    setAnswers({
      ...answers,
      [field]: value
    });
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    } else {
      setShowImport(true);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    }
  };

  const handleYesClick = () => {
    setHasImplemented(true);
    setShowQuestionnaire(true);
  };

  // Progress indicator
  const renderProgressIndicator = () => {
    return (
      <div className="progress-indicator">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>1</div>
        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>2</div>
        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
      </div>
    );
  };

  // Questionnaire rendering according to step
  const renderQuestionnaire = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 1: Current state - Use of Kanban today</h2>
            
            <div className="question-group">
              <h3 className="question-group-title">Physical flow management</h3>
              
              <div className="question-item">
                <label htmlFor="fluxTypes">Which types of flows are currently managed with Kanban (raw materials, parts, WIP, finished goods)?</label>
                <textarea 
                  id="fluxTypes" 
                  value={answers.fluxTypes} 
                  onChange={(e) => handleAnswerChange('fluxTypes', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="kanbanSystem">Do you use a physical Kanban system (cards, bins) or an electronic one (e-Kanban, ERP, mobile apps)?</label>
                <textarea 
                  id="kanbanSystem" 
                  value={answers.kanbanSystem} 
                  onChange={(e) => handleAnswerChange('kanbanSystem', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="kanbanLoops">How many Kanban loops exist in your organization?</label>
                <textarea 
                  id="kanbanLoops" 
                  value={answers.kanbanLoops} 
                  onChange={(e) => handleAnswerChange('kanbanLoops', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="cardsNumber">How is the number of cards or stock level per loop defined?</label>
                <textarea 
                  id="cardsNumber" 
                  value={answers.cardsNumber} 
                  onChange={(e) => handleAnswerChange('cardsNumber', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="kanbanUnits">What are the Kanban units used (fixed quantity, container, pallet, product, etc.)?</label>
                <textarea 
                  id="kanbanUnits" 
                  value={answers.kanbanUnits} 
                  onChange={(e) => handleAnswerChange('kanbanUnits', e.target.value)}
                  rows="3"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">Operating logic</h3>
              
              <div className="question-item">
                <label htmlFor="replenishmentFrequency">What is the replenishment frequency in your Kanban cycle?</label>
                <textarea 
                  id="replenishmentFrequency" 
                  value={answers.replenishmentFrequency} 
                  onChange={(e) => handleAnswerChange('replenishmentFrequency', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="cardsRules">Do you have clear rules for withdrawing and returning Kanban cards?</label>
                <textarea 
                  id="cardsRules" 
                  value={answers.cardsRules} 
                  onChange={(e) => handleAnswerChange('cardsRules', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="stockoutManagement">How do you handle stockouts or early stockout risks?</label>
                <textarea 
                  id="stockoutManagement" 
                  value={answers.stockoutManagement} 
                  onChange={(e) => handleAnswerChange('stockoutManagement', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="loopsAdjustment">Are Kanban loops adjusted based on actual demand (Takt Time, seasonality, etc.)?</label>
                <textarea 
                  id="loopsAdjustment" 
                  value={answers.loopsAdjustment} 
                  onChange={(e) => handleAnswerChange('loopsAdjustment', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="replenishmentDelay">What are the average replenishment lead times on your current loops?</label>
                <textarea 
                  id="replenishmentDelay" 
                  value={answers.replenishmentDelay} 
                  onChange={(e) => handleAnswerChange('replenishmentDelay', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title"> Part 2: Desired future state - Target vision of the Kanban system</h2>
            
            <div className="question-group">
              <h3 className="question-group-title"> Objectives and ambitions</h3>
              
              <div className="question-item">
                <label htmlFor="performanceObjectives">What performance objectives do you aim to achieve with Kanban (zero stockout, pull flow, stock reduction, flow improvements)?</label>
                <textarea 
                  id="performanceObjectives" 
                  value={answers.performanceObjectives} 
                  onChange={(e) => handleAnswerChange('performanceObjectives', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="kanbanExtension">Do you plan to extend Kanban to other flows or departments (maintenance, logistics, support functions)?</label>
                <textarea 
                  id="kanbanExtension" 
                  value={answers.kanbanExtension} 
                  onChange={(e) => handleAnswerChange('kanbanExtension', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="mrpReplacement">Do you intend to replace an MRP system with Kanban for some products?</label>
                <textarea 
                  id="mrpReplacement" 
                  value={answers.mrpReplacement} 
                  onChange={(e) => handleAnswerChange('mrpReplacement', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="stockTargets">What is your target stock level or expected inventory reduction?</label>
                <textarea 
                  id="stockTargets" 
                  value={answers.stockTargets} 
                  onChange={(e) => handleAnswerChange('stockTargets', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="digitalization">Do you plan to further digitalize your system (e-Kanban, ERP API, QR/NFC scanning)?</label>
                <textarea 
                  id="digitalization" 
                  value={answers.digitalization} 
                  onChange={(e) => handleAnswerChange('digitalization', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 3: Monitoring, gaps and continuous improvement</h2>
            
            <div className="question-group">
              <h3 className="question-group-title">KPIs and governance</h3>
              
              <div className="question-item">
                <label htmlFor="kanbanKPIs">Which KPIs are monitored to evaluate Kanban performance (stockout rate, card reuse rate, cycle time, average stock)?</label>
                <textarea 
                  id="kanbanKPIs" 
                  value={answers.kanbanKPIs} 
                  onChange={(e) => handleAnswerChange('kanbanKPIs', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="loopsResponsible">Who is responsible for the daily monitoring of Kanban loops?</label>
                <textarea 
                  id="loopsResponsible" 
                  value={answers.loopsResponsible} 
                  onChange={(e) => handleAnswerChange('loopsResponsible', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="anomaliesManagement">How are incidents or anomalies recorded and resolved?</label>
                <textarea 
                  id="anomaliesManagement" 
                  value={answers.anomaliesManagement} 
                  onChange={(e) => handleAnswerChange('anomaliesManagement', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="parametersReview">How often are Kanban loop parameters reviewed?</label>
                <textarea 
                  id="parametersReview" 
                  value={answers.parametersReview} 
                  onChange={(e) => handleAnswerChange('parametersReview', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="implementedImprovements">What improvements have already been implemented in your Kanban system over the past 12 months?</label>
                <textarea 
                  id="implementedImprovements" 
                  value={answers.implementedImprovements} 
                  onChange={(e) => handleAnswerChange('implementedImprovements', e.target.value)}
                  rows="3"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">Organization and culture</h3>
              
              <div className="question-item">
                <label htmlFor="operatorsTraining">Are operators/handlers trained on Kanban rules and discipline?</label>
                <textarea 
                  id="operatorsTraining" 
                  value={answers.operatorsTraining} 
                  onChange={(e) => handleAnswerChange('operatorsTraining', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="suppliersIntegration">Are external suppliers or logistics partners integrated into the Kanban system?</label>
                <textarea 
                  id="suppliersIntegration" 
                  value={answers.suppliersIntegration} 
                  onChange={(e) => handleAnswerChange('suppliersIntegration', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="visualSupport">Do you have effective visual support (boards, markings, color labels, etc.) for field management?</label>
                <textarea 
                  id="visualSupport" 
                  value={answers.visualSupport} 
                  onChange={(e) => handleAnswerChange('visualSupport', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="culturalResistance">Do you face cultural or organizational resistance to using Kanban?</label>
                <textarea 
                  id="culturalResistance" 
                  value={answers.culturalResistance} 
                  onChange={(e) => handleAnswerChange('culturalResistance', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="roadmap">What is your roadmap to stabilize, simplify or automate the Kanban system within the next 6-12 months?</label>
                <textarea 
                  id="roadmap" 
                  value={answers.roadmap} 
                  onChange={(e) => handleAnswerChange('roadmap', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleDownloadPDF = async () => {
  try {
    const element = document.querySelector(".kanban-page");
    if (!element) {
      alert("âŒ Element not found!");
      return;
    }

    document.querySelectorAll("*").forEach((el) => {
      const bgImage = window.getComputedStyle(el).backgroundImage;
      if (bgImage && bgImage.includes("gradient")) {
        el.style.backgroundImage = "none";
      }

      const bgColor = window.getComputedStyle(el).backgroundColor;
      if (bgColor.includes("var(") || bgColor === "transparent" || bgColor === "rgba(0, 0, 0, 0)") {
        el.style.backgroundColor = "#ffffff";
      }
    });

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    const date = new Date().toISOString().slice(0, 10);
    pdf.save(`Kanban_${date}.pdf`);
    alert("âœ… PDF generated successfully!");
  } catch (err) {
    console.error("PDF Error:", err);
    alert("âŒ PDF Error: " + (err.message || "unknown error"));
  }
};


  const saveLeanAnswers = async (moduleName) => {
        try {
          const caseId = localStorage.getItem("caseId");
          if (!caseId) {
            alert("No active case found!");
            console.warn("LocalStorage is empty. Current content:", localStorage);
            return;
          }

          console.log(`Saving ${moduleName} answers for case ${caseId}`);

          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lean/${moduleName}/save`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              case_id: caseId,
              answers: answers,
            }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.detail || `Save error (${response.status}) : ${data.message}`);
          }

          alert(`âœ… ${moduleName.toUpperCase()} answers saved successfully!`);

          handleNextStep();

        } catch (error) {
          console.error("âŒ saveLeanAnswers Error:", error);
          alert("âŒ " + error.message);
        }
  };

  return (
    <div className="container-center-horizontal">
      <div className="kanban-page screen">
        <div className="header">
          <Link to="/home">
            <img
              className="logo-icam"
              src="/img/1200px-logo-icam---2008-1@2x.png"
              alt="Logo ICAM"
            />
          </Link>
        </div>
        
        <div className="questionnaire-container">
          <h1 className="question-title">Kanban Methodology - Flow Management</h1>
          
          {!showQuestionnaire ? (
            <div className="question-section">
              <p className="question-text">
                Has your company already implemented the Kanban methodology?
              </p>
              
              <div className="buttons-container">
                <button 
                  className={`choice-button ${hasImplemented ? 'active' : ''}`}
                  onClick={handleYesClick}
                >
                  Yes
                </button>
                <button 
                  className={`choice-button ${!hasImplemented ? 'active' : ''}`}
                  onClick={() => setHasImplemented(false)}
                >
                  No
                </button>
              </div>

              {!hasImplemented && (
                <div className="redirect-section">
                  <p className="redirect-text">
                    We recommend starting by understanding the fundamentals of Lean:
                  </p>
                  <Link to="/visual-management-1" className="redirect-button">
                    Go to Visual Management method
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <>
              {!showImport ? (
                <div className="questionnaire-container-inner" ref={questionnaireRef}>
                  {renderProgressIndicator()}
                  {renderQuestionnaire()}
                  
                  <div className="navigation-buttons">
                    {currentStep > 1 && (
                      <button 
                        className="nav-button prev-button"
                        onClick={handlePrevStep}
                      >
                        Previous
                      </button>
                    )}
                    
                    <button 
                      className="nav-button next-button"
                      onClick={() => saveLeanAnswers("kanban")}
                    >
                      {currentStep < 3 ? "Next" : "Finish"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="file-upload-section">
                  <h2 className="upload-title">Import your existing documents</h2>
                  <p className="upload-description">
                    You can now upload your existing Kanban documents, dashboards, or tracking forms:
                  </p>
                  
                  <div className="upload-container">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden-file-input"
                      accept=".pdf,.jpg,.jpeg,.png,.svg,.xlsx,.xls,.doc,.docx"
                      onChange={handleFileUpload}
                      disabled={uploadStatus.isUploading}
                    />
                    
                    <button 
                      className="import-button"
                      onClick={handleImportClick}
                      disabled={uploadStatus.isUploading}
                    >
                      {uploadStatus.isUploading ? 'Uploadingâ€¦' : 'Upload a file'}
                    </button>
                    
                    {uploadStatus.isUploading && (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-bar-fill" 
                            style={{ width: `${uploadStatus.progress}%` }}
                          ></div>
                        </div>
                        <p className="progress-text">Uploadingâ€¦ {uploadStatus.progress}%</p>
                      </div>
                    )}

                    {uploadStatus.error && (
                      <div className="upload-error">
                        <p>{uploadStatus.error}</p>
                      </div>
                    )}

                    {uploadStatus.success && (
                      <div className="upload-success">
                        <p>The file {uploadStatus.fileName} was uploaded successfully!</p>
                        {uploadStatus.fileId && (
                          <button 
                            className="download-button"
                            onClick={() => window.open(`/api/kanban/${uploadStatus.fileId}`, '_blank')}
                          >
                            View file
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="navigation-buttons">
                    <button 
                      className="nav-button prev-button"
                      onClick={() => setShowImport(false)}
                    >
                      Back to questionnaire
                    </button>
                    
                    <Link to="/kanban-2" className="nav-button next-button">
                      Continue
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <button 
            className="download-button"
            onClick={handleDownloadPDF}
            disabled={uploadStatus.isUploading}
          >
            {uploadStatus.isUploading ? "Generating PDFâ€¦" : "Download PDF"}
          </button>
      </div>
    </div>
  );
}

export default Kanban1;
